import React, { useState } from 'react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ExpandableText from '../../components/ui/ExpandableText';
import PdfLibrary from '../../components/PdfLibrary/PdfLibrary';
import clubLectureData from '../../data/clubLectureData.json';
import {
  BookOpenIcon,
  StarIcon,
  NewspaperIcon,
  ArrowRightIcon,
  ChatBubbleBottomCenterTextIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

export default function ClubLecturePage() {
  const [showLibrary, setShowLibrary] = useState(false);

  return (
    <div className="max-w-5xl mx-auto space-y-12 animate-fade-in px-4">
      {/* Hero */}
      <Card variant="elevated" className="p-6 sm:p-10 flex flex-col md:flex-row items-center gap-8">
        <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden border-4 border-white shadow-acem-md flex-shrink-0 bg-acem-surface-container-low">
          <img
            src="/images/galerie/img_titanic.jpeg"
            alt="Club de Lecture ACEM Fès"
            className="w-full h-full object-cover object-bottom"
          />
        </div>
        <div className="flex-1 space-y-3 text-center md:text-left">
          <Badge variant="accent">ACEM Fès Culture & Littérature</Badge>
          <h1 className="font-title text-2xl sm:text-4xl font-extrabold text-acem-primary-dark">
            Club de Lecture
          </h1>
          <p className="text-xs sm:text-sm text-acem-text/85 leading-relaxed">
            Le Club de Lecture est un espace de découverte, d'échange et de partage autour du plaisir de lire. Ouvert à tous les passionnés, il permet de développer son esprit critique, de débattre d'ouvrages stimulants et de cultiver le goût de la lecture dans une ambiance conviviale.
          </p>
        </div>
      </Card>

      {/* Bouton pour afficher la bibliothèque de livres */}
      <div className="text-center">
        <Button
          variant={showLibrary ? "secondary" : "primary"}
          size="lg"
          onClick={() => setShowLibrary((prev) => !prev)}
          className="inline-flex items-center gap-2"
        >
          <BookOpenIcon className="w-5 h-5" aria-hidden="true" />
          <span>{showLibrary ? "Masquer les livres PDF" : "Explorer les livres de la bibliothèque"}</span>
        </Button>
      </div>

      {showLibrary && (
        <div className="pt-4 border-t border-acem-border">
          <PdfLibrary fixedCategory="livres" />
        </div>
      )}

      {/* Livre du Mois */}
      {clubLectureData.livreDuMois && (
        <Card variant="default" className="p-6 sm:p-8 flex flex-col md:flex-row items-center gap-8 bg-gradient-to-br from-emerald-50/50 to-amber-50/30">
          <img
            src={clubLectureData.livreDuMois.image}
            alt={clubLectureData.livreDuMois.titre}
            className="w-40 rounded-xl shadow-acem-md flex-shrink-0"
          />
          <div className="space-y-3 flex-1 text-center md:text-left">
            <Badge variant="success">
              <StarIcon className="w-3.5 h-3.5 mr-1 inline-block" aria-hidden="true" />
              Le Livre du Mois
            </Badge>
            <h2 className="font-title text-2xl sm:text-3xl font-bold text-acem-primary-dark">
              "{clubLectureData.livreDuMois.titre}"
            </h2>
            <p className="text-sm font-semibold text-acem-accent">
              Par {clubLectureData.livreDuMois.auteur}
            </p>
            <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed">
              {clubLectureData.livreDuMois.description}
            </p>
          </div>
        </Card>
      )}

      {/* Livres Proposés */}
      {clubLectureData.livresProposes && clubLectureData.livresProposes.length > 0 && (
        <section className="space-y-4">
          <div className="border-b border-acem-border pb-2">
            <h3 className="font-title text-xl font-bold text-acem-primary-dark flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-acem-primary" aria-hidden="true" />
              <span>Livres à Découvrir</span>
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
            {clubLectureData.livresProposes.map((livre, idx) => (
              <div
                key={idx}
                className="group relative aspect-[2/3] w-full rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1.5 border border-black/10 bg-acem-surface-container-high cursor-pointer"
              >
                {/* Couverture du livre en pleine surface */}
                <img
                  src={livre.image}
                  alt={livre.titre}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 ease-out"
                  loading="lazy"
                />

                {/* Overlay dégradé noir -> transparent */}
                <div
                  className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 via-45% to-black/15 group-hover:via-black/70 transition-colors duration-300 pointer-events-none"
                  aria-hidden="true"
                />

                {/* Contenu textuel blanc posé sur l'image */}
                <div className="absolute inset-x-0 bottom-0 p-3.5 sm:p-5 z-10 flex flex-col justify-end space-y-1">
                  <h4 className="font-title font-bold text-sm sm:text-base text-white leading-snug line-clamp-2 drop-shadow-md group-hover:text-amber-200 transition-colors">
                    {livre.titre}
                  </h4>
                  <p className="text-xs font-semibold text-white/85 line-clamp-1 drop-shadow-sm">
                    {livre.auteur}
                  </p>
                  {livre.description && (
                    <p className="text-[11px] text-white/70 line-clamp-2 leading-relaxed pt-0.5 drop-shadow-sm">
                      {livre.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Articles Proposés */}
      {clubLectureData.articlesProposes && clubLectureData.articlesProposes.length > 0 && (
        <section className="space-y-4">
          <div className="border-b border-acem-border pb-2">
            <h3 className="font-title text-xl font-bold text-acem-primary-dark flex items-center gap-2">
              <NewspaperIcon className="w-5 h-5 text-acem-primary" aria-hidden="true" />
              <span>Articles & Chroniques</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {clubLectureData.articlesProposes.map((art, idx) => (
              <div
                key={idx}
                className="group relative aspect-[4/5] sm:aspect-[3/4] w-full rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1.5 border border-black/10 bg-acem-surface-container-high"
              >
                {/* Image de fond de l'article en pleine surface */}
                <img
                  src={art.image}
                  alt={art.titre}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 ease-out"
                  loading="lazy"
                />

                {/* Overlay dégradé noir -> transparent */}
                <div
                  className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 via-50% to-black/20 group-hover:via-black/70 transition-colors duration-300 pointer-events-none"
                  aria-hidden="true"
                />

                {/* Badge éditorial en haut */}
                <div className="absolute top-3.5 left-3.5 z-10">
                  <span className="text-[10px] uppercase tracking-wider font-extrabold px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-white/90 border border-white/20">
                    Chronique
                  </span>
                </div>

                {/* Contenu textuel blanc superposé en bas */}
                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 z-10 flex flex-col justify-end space-y-1.5">
                  <h4 className="font-title font-bold text-base sm:text-lg text-white leading-snug line-clamp-2 drop-shadow-md group-hover:text-amber-200 transition-colors">
                    {art.titre}
                  </h4>
                  <p className="text-xs font-semibold text-white/85 line-clamp-1 drop-shadow-sm">
                    Par {art.auteur}
                  </p>
                  {art.lien && (
                    <div className="pt-2">
                      <a
                        href={art.lien}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-white/15 hover:bg-acem-primary text-white backdrop-blur-md border border-white/25 hover:border-acem-primary transition-all duration-300 shadow-sm group/btn"
                      >
                        <span>Lire l'article complet</span>
                        <ArrowRightIcon className="w-3.5 h-3.5 transition-transform group-hover/btn:translate-x-1" aria-hidden="true" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Citations */}
      {clubLectureData.citations && clubLectureData.citations.length > 0 && (
        <section className="space-y-4">
          <div className="border-b border-acem-border pb-2">
            <h3 className="font-title text-xl font-bold text-acem-primary-dark flex items-center gap-2">
              <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-acem-primary" aria-hidden="true" />
              <span>Citations Inspirantes</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {clubLectureData.citations.map((cit, idx) => (
              <Card key={idx} variant="default" className="p-5 space-y-2 border-l-4 border-acem-primary">
                <p className="text-xs sm:text-sm text-acem-text italic leading-relaxed">
                  « {cit.texte} »
                </p>
                <p className="text-xs font-bold text-acem-primary text-right">
                  — {cit.auteur}
                </p>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Rejoindre le club CTA */}
      <Card variant="glass" className="p-8 text-center space-y-3 max-w-2xl mx-auto">
        <h3 className="font-title text-2xl font-bold text-acem-primary-dark">
          Envie de rejoindre le Club de Lecture ?
        </h3>
        <p className="text-xs sm:text-sm text-acem-muted max-w-md mx-auto leading-relaxed">
          Participez à nos prochaines séances d'échange et proposez vos lectures préférées.
        </p>
        <div className="pt-2">
          <Button
            as="a"
            href="mailto:acemfes7@gmail.com"
            variant="primary"
            size="md"
            className="inline-flex items-center gap-2"
          >
            <EnvelopeIcon className="w-4 h-4" aria-hidden="true" />
            <span>Contactez-nous : acemfes7@gmail.com</span>
          </Button>
        </div>
      </Card>
    </div>
  );
}
